import { HHStack, HHTypography } from '@hinge-health/react-component-library';
import { FormEvent, MouseEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClientInsurerSearch } from '../components/client-insurers/client-insurer-search';
import { ClientSearch } from '../components/clients/client-search';
import { QuickActionsGrid } from '../components/quick-actions-grid';
import UserSearch from '../components/user/user-search';
import { routes } from '../constants/strings/routes';
import { useSnackbarContext } from '../context/snackbar.context';
import {
  BatchClientInsurerType,
  ClientType,
  useGetClientInsurersByIdsQuery,
} from '../types';
import { selectSorterFunction } from '../utils/bills-utils';

const BillingToolHomePage = (): JSX.Element => {
  const navigation = useNavigate();
  const [clientInsurerId, setClientInsurerId] = useState('');
  const [clientId, setClientId] = useState('');
  const isDisabled = !clientInsurerId || parseInt(clientInsurerId, 10) === 0;
  const isClientDisabled = !clientId || parseInt(clientId, 10) === 0;
  const [sortedClients, setSortedClients] = useState<ClientType[]>([]);
  const [sortedClientInsurers, setSortedClientInsurers] = useState<
    BatchClientInsurerType[]
  >([]);
  const { setSnackbarState } = useSnackbarContext();
  const {
    data: clientInsurersData,
    error: clientInsurersError,
    loading: clientInsurersLoading,
  } = useGetClientInsurersByIdsQuery();

  /* istanbul ignore next */
  const goToInsurerPage = (
    e: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>,
  ): void => {
    e.preventDefault();
    if (!isDisabled) {
      navigation(`${routes.billing.main}/${clientInsurerId}`);
    }
  };

  /* istanbul ignore next */
  const goToClientPage = (
    e: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>,
  ): void => {
    e.preventDefault();
    if (!isClientDisabled) {
      navigation(
        `${routes.billing.home}/${routes.billing.clientPath}/${clientId}`,
      );
    }
  };

  useEffect(() => {
    if (clientInsurersData && !clientInsurersLoading) {
      const clientDataAlt = new Set(
        clientInsurersData?.getClientInsurersByIds.map(ci => ci.client),
      );
      setSortedClients(
        [...clientDataAlt].sort((a, b) =>
          selectSorterFunction(a.identifier, b.identifier),
        ),
      );
      setSortedClientInsurers(
        [...clientInsurersData.getClientInsurersByIds].sort((a, b) =>
          selectSorterFunction(a.client.identifier, b.client.identifier),
        ),
      );
    }
  }, [clientInsurersData, clientInsurersLoading]);

  useEffect(() => {
    if (clientInsurersError) {
      setSnackbarState('Error loading client and insurer data', 'error', 3000);
    }
  }, [clientInsurersError, setSnackbarState]);

  return (
    <HHStack direction="column" sx={{ margin: 4 }} spacing={4} padding={4}>
      <HHTypography hhVariant="section-title">Billing</HHTypography>
      <HHStack spacing={2}>
        <HHStack
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          <ClientSearch
            sortedClients={sortedClients}
            clientInsurersLoading={clientInsurersLoading}
            handleSearch={goToClientPage}
            setClientId={setClientId}
            isClientDisabled={isClientDisabled}
            compactMode={false}
          />
          <ClientInsurerSearch
            sortedClientInsurers={sortedClientInsurers}
            clientInsurersLoading={clientInsurersLoading}
            handleSearch={goToInsurerPage}
            setClientInsurerId={setClientInsurerId}
            isClientInsurerDisabled={isDisabled}
            compactMode={false}
          />
          <UserSearch />
        </HHStack>
      </HHStack>
      <QuickActionsGrid />
    </HHStack>
  );
};

export default BillingToolHomePage;
